import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PersonFields from "../../features/persons/components/PersonFields";
import { updatePerson } from "../../features/persons/service/personService";
import { Person as DirectoryPerson } from "../../features/persons/types";
import { SharedMUIList } from "../common/shared/components/SharedMUIList";

type RelationEditor = {
  value: string;
  options: string[];
  onChange: (newRelation: string) => Promise<void>;
};

type PersonEditDialogProps = {
  open: boolean;
  title: string;
  person: DirectoryPerson | null;
  onClose: () => void;
  onSaved: () => void;
  // When set (Guardian/Emergency only — Father/Mother have no relation concept), also
  // shows a Relation select and, on save, pushes a relation change via relation.onChange.
  relation?: RelationEditor;
  // Set by the caller when this person currently fills a role (guardian/emergency contact)
  // that requires a mobile number — blocks the save instead of silently allowing it to be cleared.
  mobileRequired?: boolean;
  mobileRequiredMessage?: string;
};

// Edits an existing Person's own fields in place — the same form and PATCH /persons/{externalId}
// endpoint the Persons Directory's Edit action uses, just triggered from here for convenience.
// Optionally also edits the relation label stored on the Student record (Guardian/Emergency only).
export default function PersonEditDialog({
  open,
  title,
  person,
  onClose,
  onSaved,
  relation,
  mobileRequired,
  mobileRequiredMessage,
}: PersonEditDialogProps) {
  const [form, setForm] = useState<DirectoryPerson | null>(person);
  const [relationValue, setRelationValue] = useState<string>(relation?.value ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Keyed on person?.externalId rather than the person object itself: StudentDetails passes a
  // freshly-created object on every render (toDirectoryPerson(dbStudent.father) etc.), so keying
  // on object identity would reset the in-progress edit whenever the parent re-renders for any
  // unrelated reason while this dialog is open.
  useEffect(() => {
    setForm(person);
    setRelationValue(relation?.value ?? "");
    setSaveError(null);
  }, [person?.externalId, open]);

  const handleSave = async () => {
    if (!person?.externalId || !form) return;

    if (mobileRequired && (!form.mobileNo || form.mobileNo.trim().length === 0)) {
      setSaveError(mobileRequiredMessage || "A mobile number is required while this person is an assigned contact.");
      return;
    }

    const changedFields: Partial<DirectoryPerson> = {};
    (Object.keys(form) as (keyof DirectoryPerson)[]).forEach((field) => {
      if (form[field] !== person[field]) {
        (changedFields as any)[field] = form[field];
      }
    });

    const relationChanged = relation && relationValue !== relation.value;

    if (Object.keys(changedFields).length === 0 && !relationChanged) {
      onClose();
      return;
    }

    setSaveError(null);
    setSaving(true);
    try {
      if (Object.keys(changedFields).length > 0) {
        await updatePerson(person.externalId, changedFields);
      }
      if (relationChanged) {
        await relation.onChange(relationValue);
      }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>{title}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {relation && (
          <SharedMUIList lable="Relation" value={relationValue} list={relation.options} onChange={setRelationValue} />
        )}
        {form && <PersonFields value={form} onChange={setForm} />}
        {saveError && (
          <Typography variant="body2" color="error">
            {saveError}
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
